import React, { useState, useEffect } from 'react';
import {
  FiTrendingUp,
  FiUsers,
  FiActivity,
  FiTarget,
  FiBarChart2,
  FiPieChart,
  FiDownload,
  FiFilter,
  FiCalendar,
  FiRefreshCw
} from 'react-icons/fi';
import AdvancedAnalyticsChart from './AdvancedAnalyticsChart';
import RealTimeMetrics from './RealTimeMetrics';
import TimeframeSelector from './TimeframeSelector';
import api from '../services/api';

export default function AdvancedAnalyticsDashboard() {
  const [timeframe, setTimeframe] = useState('7d');
  const [analyticsData, setAnalyticsData] = useState({
    userEngagement: null,
    contentPerformance: null,
    systemMetrics: null,
    cohortAnalysis: null,
    conversionFunnel: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch advanced analytics data
  const fetchAdvancedAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/admin/analytics/advanced', {
        params: { timeframe }
      });
      
      setAnalyticsData(response.data);
      
    } catch (err) {
      setError('Failed to load advanced analytics data');
      console.error('Advanced analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvancedAnalytics();
  }, [timeframe]);

  // Mock data generation for demo purposes
  const generateMockData = () => {
    const days = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : timeframe === '90d' ? 90 : 365;
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() - days);
    
    return {
      userEngagement: {
        dailyActiveUsers: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 500 + 1200 + i * 10)
        })),
        sessionDuration: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 10 + 15)
        })),
        pageViews: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 2000 + 5000 + i * 50)
        }))
      },
      contentPerformance: {
        topCategories: [
          { label: 'JavaScript', value: 2840, color: '#F7DF1E' },
          { label: 'Python', value: 2340, color: '#3776AB' },
          { label: 'React', value: 1890, color: '#61DAFB' },
          { label: 'Node.js', value: 1456, color: '#339933' },
          { label: 'CSS', value: 1203, color: '#1572B6' },
          { label: 'HTML', value: 987, color: '#E34F26' }
        ],
        questionDifficulty: [
          { label: 'Easy', value: 45, color: '#10B981' },
          { label: 'Medium', value: 35, color: '#F59E0B' },
          { label: 'Hard', value: 20, color: '#EF4444' }
        ],
        completionRates: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 15 + 75)
        }))
      },
      systemMetrics: {
        responseTime: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 50 + 100)
        })),
        errorRate: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.random() * 2 + 0.5
        })),
        serverLoad: Array.from({ length: days }, (_, i) => ({
          label: new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString(),
          value: Math.floor(Math.random() * 30 + 40)
        }))
      },
      conversionFunnel: [
        { label: 'Visitors', value: 10000, color: '#3B82F6' },
        { label: 'Sign Ups', value: 3500, color: '#8B5CF6' },
        { label: 'First Quiz', value: 2800, color: '#10B981' },
        { label: 'Completed Quiz', value: 2100, color: '#F59E0B' },
        { label: 'Subscribed', value: 450, color: '#EF4444' }
      ]
    };
  };

  const mockData = generateMockData();
  const displayData = analyticsData.userEngagement ? analyticsData : mockData;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'content', label: 'Content', icon: FiTarget },
    { id: 'system', label: 'System', icon: FiActivity },
    { id: 'conversion', label: 'Conversion', icon: FiTrendingUp }
  ];

  const exportData = () => {
    const dataToExport = {
      timeframe,
      exportedAt: new Date().toISOString(),
      data: displayData
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `advanced_analytics_${timeframe}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <FiRefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <div>Loading advanced analytics...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1F2937' }}>
            Advanced Analytics
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#6B7280', fontSize: '0.875rem' }}>
            Deep insights into user behavior, content performance, and system metrics
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <TimeframeSelector
            value={timeframe}
            onChange={setTimeframe}
            options={[
              { value: '7d', label: 'Last 7 days' },
              { value: '30d', label: 'Last 30 days' },
              { value: '90d', label: 'Last 90 days' },
              { value: '1y', label: 'Last year' }
            ]}
          />
          
          <button
            onClick={fetchAdvancedAnalytics}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <FiRefreshCw size={14} />
            Refresh
          </button>

          <button
            onClick={exportData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <FiDownload size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: '1rem',
          borderRadius: '8px'
        }}>
          {error}
        </div>
      )}

      {/* Real-Time Metrics */}
      <RealTimeMetrics />

      {/* Navigation Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB'
        }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 1.5rem',
                  backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                  color: activeTab === tab.id ? '#3B82F6' : '#6B7280',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #3B82F6' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '1.5rem' }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Daily Active Users"
                  data={displayData.userEngagement.dailyActiveUsers}
                  type="area"
                  color="#3B82F6"
                  showDownload={true}
                  showInfo={true}
                  infoText="Number of unique users who engaged with the platform each day"
                />
                <AdvancedAnalyticsChart
                  title="Quiz Completion Rate"
                  data={displayData.contentPerformance.completionRates}
                  type="line"
                  color="#10B981"
                  showDownload={true}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Top Quiz Categories"
                  data={displayData.contentPerformance.topCategories}
                  type="donut"
                  showDownload={true}
                />
                <AdvancedAnalyticsChart
                  title="User Conversion Funnel"
                  data={displayData.conversionFunnel}
                  type="bar"
                  color="#8B5CF6"
                  showDownload={true}
                />
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Daily Active Users"
                  data={displayData.userEngagement.dailyActiveUsers}
                  type="area"
                  color="#3B82F6"
                  showDownload={true}
                />
                <AdvancedAnalyticsChart
                  title="Average Session Duration (minutes)"
                  data={displayData.userEngagement.sessionDuration}
                  type="line"
                  color="#F59E0B"
                  showDownload={true}
                />
              </div>
              <AdvancedAnalyticsChart
                title="Page Views"
                data={displayData.userEngagement.pageViews}
                type="bar"
                color="#10B981"
                showDownload={true}
              />
            </div>
          )}

          {activeTab === 'content' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Quiz Categories Performance"
                  data={displayData.contentPerformance.topCategories}
                  type="donut"
                  showDownload={true}
                />
                <AdvancedAnalyticsChart
                  title="Question Difficulty Distribution"
                  data={displayData.contentPerformance.questionDifficulty}
                  type="donut"
                  showDownload={true}
                />
              </div>
              <AdvancedAnalyticsChart
                title="Completion Rates Over Time"
                data={displayData.contentPerformance.completionRates}
                type="area"
                color="#8B5CF6"
                showDownload={true}
              />
            </div>
          )}

          {activeTab === 'system' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Response Time (ms)"
                  data={displayData.systemMetrics.responseTime}
                  type="line"
                  color="#EF4444"
                  showDownload={true}
                />
                <AdvancedAnalyticsChart
                  title="Error Rate (%)"
                  data={displayData.systemMetrics.errorRate}
                  type="area"
                  color="#F59E0B"
                  showDownload={true}
                />
              </div>
              <AdvancedAnalyticsChart
                title="Server Load (%)"
                data={displayData.systemMetrics.serverLoad}
                type="bar"
                color="#8B5CF6"
                showDownload={true}
              />
            </div>
          )}

          {activeTab === 'conversion' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                <AdvancedAnalyticsChart
                  title="Conversion Funnel"
                  data={displayData.conversionFunnel}
                  type="bar"
                  color="#3B82F6"
                  showDownload={true}
                />
                <div style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.5rem'
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600' }}>
                    Conversion Rates
                  </h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {displayData.conversionFunnel.map((step, index) => {
                      if (index === 0) return null;
                      const prevStep = displayData.conversionFunnel[index - 1];
                      const conversionRate = ((step.value / prevStep.value) * 100).toFixed(1);
                      
                      return (
                        <div key={step.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.5rem',
                          backgroundColor: 'white',
                          borderRadius: '4px'
                        }}>
                          <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                            {prevStep.label} → {step.label}
                          </span>
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: step.color }}>
                            {conversionRate}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}